import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ObjectPosition() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎯 Advanced CSS: Object Position</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The CSS <code>object-position</code> property specifies the alignment of
        an <code>&lt;img&gt;</code> or <code>&lt;video&gt;</code> within its
        container. It works best with <code>object-fit</code> to control
        cropping and placement.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>object-position</code> controls image alignment inside the
            container.
          </li>
          <li>
            Default value is <code>50% 50%</code> (center).
          </li>
          <li>
            Use keywords like <code>top</code>, <code>bottom</code>,{" "}
            <code>left</code>, <code>right</code> or percentages for precision.
          </li>
          <li>
            Works with all <code>object-fit</code> values except{" "}
            <code>fill</code> (because fill stretches image).
          </li>
          <li>
            Combine with <code>contain</code> or <code>cover</code> to control
            visible area.
          </li>
        </ul>
      </div>

      {/* Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Object-position Examples</h2>

        {/* Top Left */}
        <h3>1. Top Left</h3>
        <CodeCopyBlock
          code={`img { width: 300px; height: 150px; object-fit: cover; object-position: top left; }`}
        />
        <img
          src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=600&q=80"
          alt="top left"
          style={{
            width: "300px",
            height: "150px",
            objectFit: "cover",
            objectPosition: "top left",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        />

        {/* Top Right */}
        <h3>2. Top Right</h3>
        <CodeCopyBlock
          code={`img { width: 300px; height: 150px; object-fit: cover; object-position: top right; }`}
        />
        <img
          src="https://images.unsplash.com/photo-1518791841217-8f162f1e1131?auto=format&fit=crop&w=600&q=80"
          alt="top right"
          style={{
            width: "300px",
            height: "150px",
            objectFit: "cover",
            objectPosition: "top right",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        />

        {/* Center */}
        <h3>3. Center</h3>
        <CodeCopyBlock
          code={`img { width: 300px; height: 150px; object-fit: cover; object-position: center; }`}
        />
        <img
          src="https://images.unsplash.com/photo-1526772662000-3f88f10405ff?auto=format&fit=crop&w=600&q=80"
          alt="center"
          style={{
            width: "300px",
            height: "150px",
            objectFit: "cover",
            objectPosition: "center",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        />

        {/* Bottom Left */}
        <h3>4. Bottom Left</h3>
        <CodeCopyBlock
          code={`img { width: 300px; height: 150px; object-fit: cover; object-position: bottom left; }`}
        />
        <img
          src="https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=600&q=80"
          alt="bottom left"
          style={{
            width: "300px",
            height: "150px",
            objectFit: "cover",
            objectPosition: "bottom left",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        />

        {/* Bottom Right */}
        <h3>5. Bottom Right</h3>
        <CodeCopyBlock
          code={`img { width: 300px; height: 150px; object-fit: cover; object-position: bottom right; }`}
        />
        <img
          src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=600&q=80"
          alt="bottom right"
          style={{
            width: "300px",
            height: "150px",
            objectFit: "cover",
            objectPosition: "bottom right",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        />

        {/* Percentage Example */}
        <h3>6. Custom Position (percentages)</h3>
        <CodeCopyBlock
          code={`img { width: 300px; height: 150px; object-fit: cover; object-position: 30% 70%; }`}
        />
        <img
          src="https://images.unsplash.com/photo-1518791841217-8f162f1e1131?auto=format&fit=crop&w=600&q=80"
          alt="custom position"
          style={{
            width: "300px",
            height: "150px",
            objectFit: "cover",
            objectPosition: "30% 70%",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine <code>object-position</code> with{" "}
            <code>object-fit: cover</code> for precise cropping.
          </li>
          <li>Use percentages for exact control over alignment.</li>
          <li>
            Experiment with different container sizes to see how alignment
            changes.
          </li>
          <li>
            Animate <code>object-position</code> on hover for interactive
            effects.
          </li>
          <li>
            Apply on videos or dynamic content for advanced media layouts.
          </li>
        </ul>
      </div>
    </div>
  );
}
